// Result displays for a recording — parity with the JBS AiContentPanel
// (summary, show notes, key quotes, blog draft, highlights, chapters,
// thumbnails, social formats, speaker diarization) plus the transcript,
// all read from the recording's TAMS data flows via /api/processing/results.
import { useMemo, useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import {
  AlertCircle,
  BookOpen,
  Check,
  ChevronDown,
  ChevronUp,
  Clock,
  Copy,
  Download,
  FileText,
  Film,
  Image as ImageIcon,
  ListChecks,
  Loader2,
  MessageSquareText,
  Quote,
  RefreshCw,
  Scissors,
  Smartphone,
  Star,
  Users,
} from 'lucide-react';
import { Role } from '@jeket-cloud/types/auth';
import { RoleGate } from '@/components/auth/RoleGate';
import { useTamsFlows, useTamsSegments } from '@/hooks/useProcessing';
import { parseTimerange } from '@/lib/timerange';
import type {
  Chapter,
  Clip,
  DiarizationData,
  ProcessingJob,
  ProcessingResult,
  ResultOfKind,
} from '@/types/processing';
import { speakerColor } from './RecordingTimeline';
import {
  EmptyState,
  InlineError,
  StatusBadge,
  buttonSecondary,
  formatDuration,
  formatOffset,
  latestOfKind,
  resultsOfKind,
} from './shared';

function Panel({ icon: Icon, title, actions, children }: { icon: typeof FileText; title: string; actions?: ReactNode; children: ReactNode }) {
  return (
    <section className="bg-jeket-black-elevated border border-jeket-gold/20 rounded-lg p-4 sm:p-5">
      <div className="flex items-start justify-between gap-2 mb-3">
        <div className="flex items-center gap-2 text-jeket-gold">
          <Icon size={16} />
          <h2 className="text-sm font-medium uppercase tracking-wide">{title}</h2>
        </div>
        {actions && <div className="flex items-center gap-2 flex-wrap justify-end">{actions}</div>}
      </div>
      {children}
    </section>
  );
}

function CopyButton({ text, label }: { text: string; label?: string }) {
  const [copied, setCopied] = useState(false);
  return (
    <button
      type="button"
      onClick={async () => {
        try {
          await navigator.clipboard.writeText(text);
          setCopied(true);
          setTimeout(() => setCopied(false), 2000);
        } catch {
          // Clipboard permission denied: nothing useful to do.
        }
      }}
      className="flex items-center gap-1 px-2 py-1 text-xs text-jeket-silver-muted hover:text-jeket-silver-bright bg-jeket-black-deep rounded border border-jeket-gold/10 hover:border-jeket-gold/30 transition-colors"
    >
      {copied ? <Check size={12} className="text-jeket-healthy" /> : <Copy size={12} />}
      {copied ? 'Copied' : label || 'Copy'}
    </button>
  );
}

function TimeChip({ t, origin, onSeek }: { t: number; origin: number; onSeek?: (t: number) => void }) {
  const label = formatOffset(t - origin);
  if (!onSeek) {
    return <span className="px-1.5 py-0.5 text-xs bg-jeket-black-deep rounded text-jeket-gold/80 tabular-nums">{label}</span>;
  }
  return (
    <button
      type="button"
      onClick={() => onSeek(t)}
      className="px-1.5 py-0.5 text-xs bg-jeket-black-deep rounded text-jeket-gold/80 hover:text-jeket-gold tabular-nums"
      title="Seek to this point"
    >
      {label}
    </button>
  );
}

// ---- Regenerate button (write action, SiteOperator+) ----

export function RegenerateButton({
  label,
  onClick,
  pending,
  error,
}: {
  label: string;
  onClick: () => void;
  pending: boolean;
  error?: unknown;
}) {
  return (
    <RoleGate role={Role.SiteOperator}>
      <div className="flex flex-col items-end">
        <button type="button" onClick={onClick} disabled={pending} className={buttonSecondary}>
          {pending ? <Loader2 size={12} className="animate-spin" /> : <RefreshCw size={12} />}
          {pending ? 'Queuing…' : label}
        </button>
        <InlineError error={error} />
      </div>
    </RoleGate>
  );
}

function JobStatusLine({ job }: { job: ProcessingJob | undefined }) {
  if (!job) return null;
  return (
    <div className="flex items-center gap-2 text-xs text-jeket-silver-muted mb-3">
      <StatusBadge status={job.status} />
      {job.status === 'running' && <span>{job.progress}%</span>}
      {job.error && <span className="text-jeket-error truncate" title={job.error}>{job.error}</span>}
    </div>
  );
}

// ---- Summary ----

export function SummaryPanel({
  results,
  origin,
  onSeek,
  latestJob,
  regenerate,
}: {
  results: ProcessingResult[] | undefined;
  origin: number;
  onSeek: (t: number) => void;
  latestJob?: ProcessingJob;
  regenerate: ReactNode;
}) {
  const [blogExpanded, setBlogExpanded] = useState(false);
  const final = latestOfKind(results, 'final_summary')?.data;
  const rolling = resultsOfKind(results, 'summary');
  const latestRolling = rolling[rolling.length - 1];

  return (
    <div className="space-y-4">
      <Panel icon={FileText} title={final ? 'Summary' : 'Rolling summary'} actions={<>
        {(final?.summary || latestRolling) && <CopyButton text={final?.summary ?? latestRolling!.data.text} />}
        {regenerate}
      </>}>
        <JobStatusLine job={latestJob} />
        {final ? (
          <>
            {final.title && <h3 className="text-xl font-bold text-jeket-silver-bright mb-3">{final.title}</h3>}
            <p className="text-sm text-jeket-silver-bright/90 leading-relaxed whitespace-pre-line">{final.summary}</p>
          </>
        ) : latestRolling ? (
          <>
            <p className="text-xs text-jeket-silver-muted mb-2">
              Live summary of {formatOffset(latestRolling.data.windowStart - origin)}–
              {formatOffset(latestRolling.data.windowEnd - origin)}. The final whole-event summary replaces it when the event ends.
            </p>
            <p className="text-sm text-jeket-silver-bright/90 leading-relaxed whitespace-pre-line">{latestRolling.data.text}</p>
          </>
        ) : (
          <EmptyState icon={FileText} title="No summary yet" hint="Rolling summaries arrive from the site while the event is live; the final summary runs when it ends." />
        )}
      </Panel>

      {final?.showNotes && final.showNotes.length > 0 && (
        <Panel icon={ListChecks} title="Show notes" actions={<CopyButton text={final.showNotes.map((n) => `- ${n}`).join('\n')} label="Copy all" />}>
          <ul className="space-y-2">
            {final.showNotes.map((note, i) => (
              <li key={i} className="flex items-start gap-2 text-sm text-jeket-silver-bright/90">
                <span className="text-jeket-gold mt-1 flex-shrink-0">&#8226;</span>
                <span>{note}</span>
              </li>
            ))}
          </ul>
        </Panel>
      )}

      {final?.keyQuotes && final.keyQuotes.length > 0 && (
        <Panel icon={Quote} title="Key quotes">
          <div className="space-y-4">
            {final.keyQuotes.map((q, i) => (
              <div key={i} className="border-l-2 border-jeket-gold/40 pl-4">
                <blockquote className="text-sm text-jeket-silver-bright/90 italic mb-1">"{q.text}"</blockquote>
                <div className="flex items-center justify-between gap-2">
                  <div className="flex items-center gap-2 text-xs text-jeket-silver-muted">
                    {q.speaker && <span className="font-medium">{q.speaker}</span>}
                    <TimeChip t={q.start} origin={origin} onSeek={onSeek} />
                  </div>
                  <CopyButton text={q.speaker ? `"${q.text}" — ${q.speaker}` : `"${q.text}"`} />
                </div>
              </div>
            ))}
          </div>
        </Panel>
      )}

      {final?.blogDraft && (
        <section className="bg-jeket-black-elevated border border-jeket-gold/20 rounded-lg">
          <button
            type="button"
            onClick={() => setBlogExpanded(!blogExpanded)}
            className="w-full flex items-center justify-between p-4 sm:p-5 hover:bg-jeket-black-surface/30 transition-colors rounded-t-lg"
          >
            <span className="flex items-center gap-2 text-jeket-gold">
              <BookOpen size={16} />
              <span className="text-sm font-medium uppercase tracking-wide">Blog draft</span>
            </span>
            {blogExpanded ? <ChevronUp size={16} className="text-jeket-silver-muted" /> : <ChevronDown size={16} className="text-jeket-silver-muted" />}
          </button>
          {blogExpanded && (
            <div className="px-5 pb-5 border-t border-jeket-gold/10 pt-4">
              <div className="flex justify-end mb-2"><CopyButton text={final.blogDraft} /></div>
              <div className="text-sm text-jeket-silver-bright/90 leading-relaxed whitespace-pre-line">{final.blogDraft}</div>
            </div>
          )}
        </section>
      )}

      {rolling.length > 1 && (
        <Panel icon={Clock} title="Rolling summaries">
          <ol className="space-y-3">
            {rolling.map((r, i) => (
              <li key={i} className="text-sm text-jeket-silver-bright/90">
                <TimeChip t={r.data.windowStart} origin={origin} onSeek={onSeek} />
                <span className="ml-2">{r.data.text}</span>
              </li>
            ))}
          </ol>
        </Panel>
      )}
    </div>
  );
}

// ---- Highlights + clips ----

/** Clip that was cut from a mark: same range within a GOP of slack. */
function clipForRange(clips: Clip[], start: number, end: number): Clip | undefined {
  const SLACK_S = 2;
  return clips
    .filter((c) => Math.abs(c.start - start) <= SLACK_S && Math.abs(c.end - end) <= SLACK_S)
    .sort((a, b) => Number(b.approved) - Number(a.approved))[0];
}

function ClipState({ clip }: { clip: Clip }) {
  if (clip.status === 'completed' && clip.url) {
    return (
      <a
        href={clip.url}
        target="_blank"
        rel="noreferrer"
        className="flex items-center gap-1 px-2 py-1 text-xs bg-jeket-healthy/10 border border-jeket-healthy/30 rounded text-jeket-healthy hover:bg-jeket-healthy/20 transition-colors"
      >
        <Download size={10} />
        {clip.approved ? 'Approved clip' : 'Download clip'}
      </a>
    );
  }
  if (clip.status === 'running') {
    return (
      <span className="flex items-center gap-1 px-2 py-1 text-xs text-jeket-info">
        <Loader2 size={10} className="animate-spin" /> Cutting…
      </span>
    );
  }
  if (clip.status === 'pending') {
    return (
      <span className="flex items-center gap-1 px-2 py-1 text-xs text-jeket-silver-muted">
        <Clock size={10} /> Queued
      </span>
    );
  }
  if (clip.status === 'failed') {
    return (
      <span className="flex items-center gap-1 px-2 py-1 text-xs text-jeket-error">
        <AlertCircle size={10} /> Cut failed
      </span>
    );
  }
  return <StatusBadge status={clip.status} />;
}

export function HighlightsPanel({
  marks,
  clips,
  origin,
  onSelect,
}: {
  marks: ResultOfKind<'highlight_marks'>[];
  clips: Clip[];
  origin: number;
  onSelect: (start: number, end: number) => void;
}) {
  const sorted = [...marks].sort((a, b) => a.start - b.start);
  return (
    <Panel icon={Film} title="Highlights">
      {sorted.length === 0 ? (
        <EmptyState icon={Film} title="No highlight marks yet" hint="The site marks highlights live; each mark is cut into a clip automatically." />
      ) : (
        <div className="space-y-3">
          {sorted.map((m, i) => {
            const clip = clipForRange(clips, m.start, m.end);
            return (
              <div key={i} className="bg-jeket-black-deep border border-jeket-gold/10 rounded-lg p-3 sm:p-4">
                <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-2 mb-2">
                  <h3 className="text-sm font-medium text-jeket-silver-bright">{m.data.label ?? `Highlight ${i + 1}`}</h3>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <span className="flex items-center gap-1 text-xs text-jeket-gold">
                      <Star size={10} className="fill-current" />
                      {Math.round(m.data.score * 100)}%
                    </span>
                    <span className="px-2 py-0.5 text-xs bg-jeket-black-elevated rounded text-jeket-silver-muted whitespace-nowrap tabular-nums">
                      {formatOffset(m.start - origin)} – {formatOffset(m.end - origin)}
                    </span>
                  </div>
                </div>
                <p className="text-xs text-jeket-silver-muted mb-2">{m.data.reason}</p>
                <div className="flex items-center justify-between gap-2">
                  <button type="button" onClick={() => onSelect(m.start, m.end)} className={buttonSecondary}>
                    <Scissors size={12} /> Select on timeline
                  </button>
                  {clip && <ClipState clip={clip} />}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </Panel>
  );
}

export function ClipsPanel({ clips, origin, onSeek }: { clips: Clip[]; origin: number; onSeek: (t: number) => void }) {
  const sorted = [...clips].sort((a, b) => a.start - b.start);
  return (
    <Panel
      icon={Smartphone}
      title="Clips & social formats"
      actions={
        <Link to="/media/clips" className={buttonSecondary}>
          Review clips
        </Link>
      }
    >
      {sorted.length === 0 ? (
        <EmptyState icon={Scissors} title="No clips yet" hint="Clips are cut from highlight marks, or from an in/out selection on the timeline." />
      ) : (
        <div className="space-y-3">
          {sorted.map((c) => (
            <div key={c.jobId} className="bg-jeket-black-deep border border-jeket-gold/10 rounded-lg p-3 sm:p-4">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                <div className="flex items-center gap-2 min-w-0">
                  <TimeChip t={c.start} origin={origin} onSeek={onSeek} />
                  <span className="text-sm text-jeket-silver-bright truncate">{c.title ?? `Clip · ${formatDuration(c.end - c.start)}`}</span>
                  {c.approved && <span className="px-1.5 py-0.5 text-[10px] rounded bg-jeket-healthy/10 text-jeket-healthy">Approved</span>}
                  {c.rejected && <span className="px-1.5 py-0.5 text-[10px] rounded bg-jeket-error/10 text-jeket-error">Rejected</span>}
                </div>
                <ClipState clip={c} />
              </div>
              {c.social.length > 0 && (
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mt-3">
                  {c.social.map((s) =>
                    s.url ? (
                      <a
                        key={s.aspect}
                        href={s.url}
                        target="_blank"
                        rel="noreferrer"
                        className="flex items-center justify-between px-3 py-2 bg-jeket-black-elevated border border-jeket-gold/10 rounded hover:border-jeket-gold/30 transition-colors text-xs text-jeket-silver-bright"
                      >
                        {s.aspect}
                        <Download size={12} className="text-jeket-gold" />
                      </a>
                    ) : (
                      <span key={s.aspect} className="flex items-center justify-between px-3 py-2 bg-jeket-black-elevated border border-jeket-gold/10 rounded text-xs text-jeket-silver-muted">
                        {s.aspect}
                        {s.status ? <StatusBadge status={s.status} /> : <Loader2 size={12} className="animate-spin" />}
                      </span>
                    ),
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </Panel>
  );
}

// ---- Chapters ----

export function ChaptersPanel({ chapters, origin, onSeek, regenerate }: { chapters: Chapter[]; origin: number; onSeek: (t: number) => void; regenerate?: ReactNode }) {
  return (
    <Panel icon={BookOpen} title="Chapters" actions={regenerate}>
      {chapters.length === 0 ? (
        <EmptyState icon={BookOpen} title="No chapters yet" hint="Chapters arrive live from the site and are refined by the final summary pass." />
      ) : (
        <div className="space-y-3">
          {chapters.map((c, i) => (
            <div key={i} className="bg-jeket-black-deep border border-jeket-gold/10 rounded-lg p-3 sm:p-4">
              <div className="flex items-start justify-between gap-3">
                <div className="flex items-center gap-2 min-w-0">
                  <span className="flex-shrink-0 w-6 h-6 flex items-center justify-center rounded bg-jeket-gold/10 text-jeket-gold text-xs font-medium">{i + 1}</span>
                  <h3 className="text-sm font-medium text-jeket-silver-bright truncate">{c.title}</h3>
                </div>
                <TimeChip t={c.start} origin={origin} onSeek={onSeek} />
              </div>
              {c.summary && <p className="text-xs text-jeket-silver-muted mt-2 ml-8">{c.summary}</p>}
            </div>
          ))}
        </div>
      )}
    </Panel>
  );
}

// ---- Speakers ----

export function SpeakersPanel({ diarization, origin }: { diarization: DiarizationData | undefined; origin: number }) {
  const speakers = diarization?.speakers ?? [];
  const total = speakers.reduce((sum, s) => sum + s.seconds, 0);
  const turns = diarization?.turns ?? [];
  const index = new Map(speakers.map((s, i) => [s.label, i]));
  const span = turns.length > 0 ? turns[turns.length - 1].end - turns[0].start : 0;

  return (
    <Panel icon={Users} title="Speaker diarization">
      {speakers.length === 0 ? (
        <EmptyState icon={Users} title="No diarization yet" hint="Speaker talk time arrives from the site while the event is live." />
      ) : (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-4">
            {speakers.map((s, i) => {
              const pct = total > 0 ? Math.round((s.seconds / total) * 100) : 0;
              return (
                <div key={s.label} className="bg-jeket-black-deep border border-jeket-gold/10 rounded-lg p-3">
                  <div className="flex items-center gap-2 mb-2">
                    <span className={`w-3 h-3 rounded-full ${speakerColor(i)}`} />
                    <span className="text-sm font-medium text-jeket-silver-bright">{s.name ?? s.label}</span>
                    {s.name && <span className="text-xs text-jeket-silver-muted">{s.label}</span>}
                  </div>
                  <div className="flex items-center justify-between text-xs text-jeket-silver-muted mb-2">
                    <span>{formatDuration(s.seconds)}</span>
                    <span>{pct}%</span>
                  </div>
                  <div className="h-1.5 bg-jeket-black-surface rounded-full overflow-hidden">
                    <div className={`h-full ${speakerColor(i)} rounded-full`} style={{ width: `${pct}%` }} />
                  </div>
                </div>
              );
            })}
          </div>
          {turns.length > 0 && span > 0 && (
            <div>
              <h3 className="text-xs font-medium text-jeket-silver-muted uppercase tracking-wide mb-2">Timeline</h3>
              <div className="relative h-6 rounded overflow-hidden bg-jeket-black-deep border border-jeket-gold/10">
                {turns.map((t, i) => (
                  <div
                    key={i}
                    className={`absolute inset-y-0 ${speakerColor(index.get(t.speaker) ?? 0)} opacity-70`}
                    style={{ left: `${((t.start - turns[0].start) / span) * 100}%`, width: `${((t.end - t.start) / span) * 100}%` }}
                    title={`${t.speaker}: ${formatOffset(t.start - origin)} – ${formatOffset(t.end - origin)}`}
                  />
                ))}
              </div>
              <div className="flex justify-between text-[10px] text-jeket-silver-muted mt-1">
                <span>{formatOffset(turns[0].start - origin)}</span>
                <span>{turns.length} turns</span>
                <span>{formatOffset(turns[turns.length - 1].end - origin)}</span>
              </div>
            </div>
          )}
        </>
      )}
    </Panel>
  );
}

// ---- Thumbnails (image flows read straight from TAMS) ----

export function useThumbnails(sourceId: string | undefined) {
  const { data: flows } = useTamsFlows(sourceId, 'thumbnail');
  const flowId = flows?.[0]?.id;
  const segments = useTamsSegments(flowId, { limit: 50 });
  const items = useMemo(
    () =>
      (segments.data ?? [])
        .map((s) => ({ t: parseTimerange(s.timerange)?.start ?? null, url: s.get_urls?.[0]?.url }))
        .filter((x): x is { t: number; url: string } => x.t != null && !!x.url)
        .sort((a, b) => a.t - b.t),
    [segments.data],
  );
  return { items, isLoading: !!flowId && segments.isLoading, isError: segments.isError };
}

export function ThumbnailsPanel({
  items,
  isLoading,
  origin,
  onSeek,
  regenerate,
}: {
  items: { t: number; url: string }[];
  isLoading: boolean;
  origin: number;
  onSeek: (t: number) => void;
  regenerate?: ReactNode;
}) {
  return (
    <Panel icon={ImageIcon} title="Thumbnails" actions={regenerate}>
      {isLoading ? (
        <div className="flex justify-center py-6"><Loader2 size={18} className="animate-spin text-jeket-gold" /></div>
      ) : items.length === 0 ? (
        <EmptyState icon={ImageIcon} title="No thumbnail candidates yet" hint="Thumbnails are picked from the programme when the event ends." />
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
          {items.map((th) => (
            <div key={th.url} className="relative group bg-jeket-black-deep border border-jeket-gold/10 rounded-lg overflow-hidden">
              <button type="button" onClick={() => onSeek(th.t)} className="block w-full aspect-video" title="Seek to this frame">
                <img src={th.url} alt={`Thumbnail at ${formatOffset(th.t - origin)}`} className="w-full h-full object-cover" loading="lazy" />
              </button>
              <span className="absolute bottom-1.5 left-1.5 px-1.5 py-0.5 text-[10px] bg-black/70 rounded text-jeket-silver-bright tabular-nums">
                {formatOffset(th.t - origin)}
              </span>
              <a
                href={th.url}
                target="_blank"
                rel="noreferrer"
                className="absolute top-1.5 right-1.5 p-1 bg-black/70 rounded text-jeket-gold opacity-0 group-hover:opacity-100 focus:opacity-100 transition-opacity"
                title="Download"
              >
                <Download size={12} />
              </a>
            </div>
          ))}
        </div>
      )}
    </Panel>
  );
}

// ---- Transcript ----

export function TranscriptPanel({
  items,
  origin,
  onSeek,
}: {
  items: ResultOfKind<'transcript'>[];
  origin: number;
  onSeek: (t: number) => void;
}) {
  const [query, setQuery] = useState('');
  const finals = items.filter((i) => i.data.isFinal);
  const q = query.trim().toLowerCase();
  const shown = q ? finals.filter((i) => i.data.text.toLowerCase().includes(q)) : finals;

  return (
    <Panel
      icon={MessageSquareText}
      title="Transcript"
      actions={finals.length > 0 && <CopyButton text={finals.map((i) => (i.data.speaker ? `${i.data.speaker}: ${i.data.text}` : i.data.text)).join('\n')} label="Copy all" />}
    >
      {finals.length === 0 ? (
        <EmptyState icon={MessageSquareText} title="No transcript yet" />
      ) : (
        <>
          <input
            type="search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search transcript…"
            aria-label="Search transcript"
            className="w-full mb-3 bg-jeket-black-deep border border-jeket-gold/20 rounded-lg px-3 py-2 text-sm text-jeket-silver-bright focus:outline-none focus:border-jeket-gold/50"
          />
          <ol className="space-y-2 max-h-[28rem] overflow-y-auto pr-1">
            {shown.map((i, n) => (
              <li key={n} className="flex items-start gap-2 text-sm">
                <TimeChip t={i.start} origin={origin} onSeek={onSeek} />
                <span className="text-jeket-silver-bright/90">
                  {i.data.speaker && <span className="text-jeket-gold/80 mr-1">{i.data.speaker}:</span>}
                  {i.data.text}
                </span>
              </li>
            ))}
            {shown.length === 0 && <li className="text-xs text-jeket-silver-muted">No lines match.</li>}
          </ol>
        </>
      )}
    </Panel>
  );
}
